/**
 * Hero copy for the service pages.
 *
 * The shared <app-banner> looks this up from the current route, which is why a
 * single hero template can serve every service page without touching any page
 * component. A slug that is not listed here (product details, internship) gets
 * `undefined` and the banner falls back to its data-driven layout.
 *
 * Keys are lower-cased route slugs. Several services are reachable under more
 * than one slug (see service-routing.module.ts), so every alias is listed.
 *
 * Everything here is descriptive copy about what a service includes. The three
 * shared stats are the figures the company already publishes on the About page
 * plus the satisfaction figure from the approved design — they are deliberately
 * kept in one place so they only ever need correcting once.
 */

export type ServiceHeroIcon =
  | 'grid'
  | 'search'
  | 'bolt'
  | 'mobile'
  | 'shield'
  | 'palette'
  | 'code'
  | 'chart'
  | 'cloud'
  | 'megaphone'
  | 'video'
  | 'mail'
  | 'card'
  | 'cog'
  | 'ai'
  | 'users';

export interface ServiceHeroPill {
  icon: ServiceHeroIcon;
  label: string;
  sub: string;
}

export interface ServiceHeroStat {
  value: string;
  label: string;
  icon: ServiceHeroIcon;
}

export interface ServiceHero {
  /** Small uppercase badge above the headline. */
  eyebrow: string;
  /** Headline, split so the second half can carry the brand gradient. */
  title: string;
  titleGrad: string;
  /** Short supporting paragraph. */
  lede: string;
  /** Four capability chips. */
  pills: ServiceHeroPill[];
}

/** Shared across every service hero. Edit once, changes everywhere. */
export const SERVICE_HERO_STATS: ServiceHeroStat[] = [
  { value: '500+', label: 'Projects Delivered', icon: 'bolt' },
  { value: '98%', label: 'Client Satisfaction', icon: 'chart' },
  { value: '10+', label: 'Years of Experience', icon: 'shield' },
];

const HERO: Record<string, ServiceHero> = {
  /* ------------------------------------------------------- web solutions -- */
  'website-designing': {
    eyebrow: 'Website Design',
    title: 'We Design Websites That',
    titleGrad: 'Drive Results',
    lede:
      'Your website is the face of your business on the internet. We create modern, ' +
      'responsive and performance-driven websites that help you stand out.',
    pills: [
      { icon: 'palette', label: 'Modern', sub: 'Design' },
      { icon: 'search', label: 'SEO', sub: 'Friendly' },
      { icon: 'bolt', label: 'Fast', sub: 'Loading' },
      { icon: 'mobile', label: 'Mobile', sub: 'Responsive' },
    ],
  },

  'software-development': {
    eyebrow: 'Software Development',
    title: 'Custom Software Built To',
    titleGrad: 'Fit Your Business',
    lede:
      'From internal tools to customer-facing platforms, we build software around the way ' +
      'your team already works — scalable, maintainable and documented.',
    pills: [
      { icon: 'code', label: 'Custom', sub: 'Architecture' },
      { icon: 'shield', label: 'Secure', sub: 'By Design' },
      { icon: 'cloud', label: 'Cloud', sub: 'Ready' },
      { icon: 'cog', label: 'Ongoing', sub: 'Support' },
    ],
  },

  'web-application-development': {
    eyebrow: 'Web Applications',
    title: 'Web Apps That Feel',
    titleGrad: 'Effortless To Use',
    lede:
      'Dashboards, portals and workflow tools engineered for speed and clarity, so the ' +
      'people using them every day actually enjoy it.',
    pills: [
      { icon: 'grid', label: 'Rich', sub: 'Dashboards' },
      { icon: 'bolt', label: 'Real-Time', sub: 'Data' },
      { icon: 'users', label: 'Role-Based', sub: 'Access' },
      { icon: 'mobile', label: 'Works', sub: 'Anywhere' },
    ],
  },

  'web-hosting-solutions': {
    eyebrow: 'Hosting & Infrastructure',
    title: 'Hosting That Keeps You',
    titleGrad: 'Online And Fast',
    lede:
      'Managed hosting with monitoring, backups and SSL handled for you — so your site ' +
      'stays quick, secure and available.',
    pills: [
      { icon: 'cloud', label: 'Managed', sub: 'Servers' },
      { icon: 'shield', label: 'SSL &', sub: 'Backups' },
      { icon: 'bolt', label: 'Optimised', sub: 'Delivery' },
      { icon: 'cog', label: 'Monitoring', sub: '& Alerts' },
    ],
  },

  ecommercesolution: {
    eyebrow: 'Ecommerce',
    title: 'Online Stores Built To',
    titleGrad: 'Convert',
    lede:
      'Catalogue, checkout, payments and logistics wired together into a storefront your ' +
      'customers trust and your team can run.',
    pills: [
      { icon: 'card', label: 'Secure', sub: 'Checkout' },
      { icon: 'grid', label: 'Catalogue', sub: 'Management' },
      { icon: 'chart', label: 'Sales', sub: 'Reporting' },
      { icon: 'mobile', label: 'Mobile', sub: 'First' },
    ],
  },

  dataentry: {
    eyebrow: 'Data Services',
    title: 'Accurate Data, Handled',
    titleGrad: 'End To End',
    lede:
      'Structured, validated and delivered in the format your systems expect — so your ' +
      'reporting starts from clean data.',
    pills: [
      { icon: 'grid', label: 'Structured', sub: 'Output' },
      { icon: 'shield', label: 'Verified', sub: 'Accuracy' },
      { icon: 'bolt', label: 'Fast', sub: 'Turnaround' },
      { icon: 'cog', label: 'Any', sub: 'Format' },
    ],
  },

  /* --------------------------------------------------------- app solutions -- */
  'android-app-development': {
    eyebrow: 'Android Development',
    title: 'Android Apps People',
    titleGrad: 'Keep Coming Back To',
    lede:
      'Native Android products built for the full range of real devices, tuned for ' +
      'battery, data and offline behaviour.',
    pills: [
      { icon: 'mobile', label: 'Native', sub: 'Performance' },
      { icon: 'bolt', label: 'Offline', sub: 'Support' },
      { icon: 'shield', label: 'Play Store', sub: 'Compliant' },
      { icon: 'chart', label: 'Analytics', sub: 'Built In' },
    ],
  },

  'ios-app-development': {
    eyebrow: 'iOS Development',
    title: 'iOS Apps That Feel',
    titleGrad: 'Truly Native',
    lede:
      'Built to Apple’s guidelines so your app looks, moves and behaves exactly the way ' +
      'iPhone and iPad users expect.',
    pills: [
      { icon: 'mobile', label: 'Swift', sub: 'Native' },
      { icon: 'palette', label: 'Apple', sub: 'Guidelines' },
      { icon: 'shield', label: 'App Store', sub: 'Ready' },
      { icon: 'bolt', label: 'Smooth', sub: '60 FPS' },
    ],
  },

  'hybrid-app-development': {
    eyebrow: 'Hybrid Development',
    title: 'One Codebase, Both',
    titleGrad: 'App Stores',
    lede:
      'Cross-platform apps that ship to Android and iOS together — faster to launch and ' +
      'cheaper to maintain over time.',
    pills: [
      { icon: 'code', label: 'Single', sub: 'Codebase' },
      { icon: 'bolt', label: 'Faster', sub: 'Launch' },
      { icon: 'mobile', label: 'Both', sub: 'Platforms' },
      { icon: 'cog', label: 'Easier', sub: 'Upkeep' },
    ],
  },

  'native-app-development': {
    eyebrow: 'Native Development',
    title: 'Native Apps Built For',
    titleGrad: 'Maximum Performance',
    lede:
      'When speed, hardware access and platform polish matter most, native is the right ' +
      'call — and we build it properly.',
    pills: [
      { icon: 'bolt', label: 'Peak', sub: 'Performance' },
      { icon: 'mobile', label: 'Full', sub: 'Hardware' },
      { icon: 'shield', label: 'Secure', sub: 'Storage' },
      { icon: 'palette', label: 'Platform', sub: 'Polish' },
    ],
  },

  /* ------------------------------------------------------ digital marketing -- */
  'search-engine-optimization': {
    eyebrow: 'Search Engine Optimization',
    title: 'Get Found By People',
    titleGrad: 'Already Searching',
    lede:
      'Technical fixes, content and authority building that move you up the results ' +
      'page — with reporting you can actually read.',
    pills: [
      { icon: 'search', label: 'Technical', sub: 'Audits' },
      { icon: 'grid', label: 'On-Page', sub: 'SEO' },
      { icon: 'megaphone', label: 'Content', sub: 'Strategy' },
      { icon: 'chart', label: 'Clear', sub: 'Reporting' },
    ],
  },

  'digital-media-marketing': {
    eyebrow: 'Digital Media Marketing',
    title: 'Campaigns That Reach The',
    titleGrad: 'Right Audience',
    lede:
      'Paid and organic media planned around your funnel, measured properly, and ' +
      'adjusted on what the numbers actually say.',
    pills: [
      { icon: 'megaphone', label: 'Paid', sub: 'Campaigns' },
      { icon: 'users', label: 'Audience', sub: 'Targeting' },
      { icon: 'palette', label: 'Creative', sub: 'Production' },
      { icon: 'chart', label: 'Performance', sub: 'Tracking' },
    ],
  },

  'social-media-promotion': {
    eyebrow: 'Social Media',
    title: 'Social Presence That',
    titleGrad: 'Builds Trust',
    lede:
      'Consistent posting, community replies and creative that sounds like your brand — ' +
      'not like a template.',
    pills: [
      { icon: 'palette', label: 'Original', sub: 'Creative' },
      { icon: 'users', label: 'Community', sub: 'Management' },
      { icon: 'megaphone', label: 'Paid', sub: 'Boosting' },
      { icon: 'chart', label: 'Monthly', sub: 'Reports' },
    ],
  },

  'bulk-sms-and-email-marketing': {
    eyebrow: 'SMS & Email Marketing',
    title: 'Reach Every Customer',
    titleGrad: 'Directly',
    lede:
      'Segmented campaigns, clean templates and delivery you can verify — for the ' +
      'messages that genuinely need to land.',
    pills: [
      { icon: 'mail', label: 'Email', sub: 'Campaigns' },
      { icon: 'mobile', label: 'Bulk', sub: 'SMS' },
      { icon: 'users', label: 'List', sub: 'Segmentation' },
      { icon: 'chart', label: 'Delivery', sub: 'Reports' },
    ],
  },

  bulksms: {
    eyebrow: 'Bulk SMS',
    title: 'Bulk SMS That Actually',
    titleGrad: 'Gets Delivered',
    lede:
      'Transactional and promotional routes, sender ID setup and delivery reporting, ' +
      'handled end to end.',
    pills: [
      { icon: 'mobile', label: 'Bulk', sub: 'Delivery' },
      { icon: 'bolt', label: 'Instant', sub: 'Sending' },
      { icon: 'shield', label: 'Verified', sub: 'Routes' },
      { icon: 'chart', label: 'Delivery', sub: 'Logs' },
    ],
  },

  /* ---------------------------------------------------------- more services -- */
  'logo-design': {
    eyebrow: 'Logo Design',
    title: 'A Mark Your Brand Can',
    titleGrad: 'Grow Into',
    lede:
      'Original concepts, refined with you, delivered in every format and size your ' +
      'brand will need.',
    pills: [
      { icon: 'palette', label: 'Original', sub: 'Concepts' },
      { icon: 'grid', label: 'Vector', sub: 'Files' },
      { icon: 'users', label: 'Guided', sub: 'Revisions' },
      { icon: 'shield', label: 'Full', sub: 'Ownership' },
    ],
  },

  'graphic-design': {
    eyebrow: 'Graphic Design',
    title: 'Design That Makes Your',
    titleGrad: 'Brand Look Sharp',
    lede:
      'Print and digital collateral built on a consistent visual system, so everything ' +
      'you publish looks like it belongs together.',
    pills: [
      { icon: 'palette', label: 'Brand', sub: 'Systems' },
      { icon: 'grid', label: 'Print &', sub: 'Digital' },
      { icon: 'bolt', label: 'Quick', sub: 'Turnaround' },
      { icon: 'shield', label: 'Source', sub: 'Files' },
    ],
  },

  'digital-visiting-card': {
    eyebrow: 'Digital Visiting Card',
    title: 'Share Your Details In',
    titleGrad: 'One Tap',
    lede:
      'A link-and-QR card that works on every phone, updates instantly, and never runs ' +
      'out like paper does.',
    pills: [
      { icon: 'mobile', label: 'One-Tap', sub: 'Sharing' },
      { icon: 'grid', label: 'QR Code', sub: 'Included' },
      { icon: 'cog', label: 'Update', sub: 'Anytime' },
      { icon: 'chart', label: 'View', sub: 'Insights' },
    ],
  },

  'video-creation': {
    eyebrow: 'Video Production',
    title: 'Video That Explains It',
    titleGrad: 'In Seconds',
    lede:
      'Scripting, motion design and edit — explainers, ads and product videos built for ' +
      'the platform they will run on.',
    pills: [
      { icon: 'video', label: 'Scripting', sub: '& Storyboard' },
      { icon: 'palette', label: 'Motion', sub: 'Graphics' },
      { icon: 'grid', label: 'Platform', sub: 'Formats' },
      { icon: 'bolt', label: 'Fast', sub: 'Revisions' },
    ],
  },
};

/**
 * Keyword → hero key, matched against the slug as a substring.
 *
 * This exists because the same component is reachable under hundreds of SEO
 * landing slugs (`Best-Website-Designing-Agency-In-Bilaspur-Chhattisgarh`,
 * `Software-Development-Companies-near-me`, …) as well as its canonical route.
 * Matching on a contained keyword means every one of those pages gets the same
 * hero as its canonical route, with no per-route configuration.
 *
 * Ordered longest-first so `web-application-development` is tested before
 * `web-development`, and the company's own `devlopment` typo is included
 * because it is baked into the live routes.
 */
const KEYWORDS: Array<[string, string]> = [
  ['bulk-sms-and-email-marketing', 'bulk-sms-and-email-marketing'],
  ['web-application-devlopment', 'web-application-development'],
  ['web-application-development', 'web-application-development'],
  ['search-engine-optimisation', 'search-engine-optimization'],
  ['search-engine-optimization', 'search-engine-optimization'],
  ['web-hosting-solutions', 'web-hosting-solutions'],
  ['digital-media-marketing', 'digital-media-marketing'],
  ['social-media-promotion', 'social-media-promotion'],
  ['native-app-development', 'native-app-development'],
  ['hybrid-app-development', 'hybrid-app-development'],
  ['digital-visiting-card', 'digital-visiting-card'],
  ['android-app-development', 'android-app-development'],
  ['software-development', 'software-development'],
  ['website-development', 'website-designing'],
  ['ios-app-development', 'ios-app-development'],
  ['website-designing', 'website-designing'],
  ['ecommercesolution', 'ecommercesolution'],
  ['website-design', 'website-designing'],
  ['web-development', 'website-designing'],
  ['graphic-design', 'graphic-design'],
  ['video-creation', 'video-creation'],
  ['logo-design', 'logo-design'],
  ['data-entry', 'dataentry'],
  ['dataentry', 'dataentry'],
  ['bulk-sms', 'bulksms'],
  ['bulksms', 'bulksms'],
];

/**
 * Resolves a hero config from a router URL. Case-tolerant, alias-tolerant, and
 * returns undefined for any page that is not a service page — which is what
 * makes the banner fall back to its original layout on Product Details and
 * Internship.
 */
export function serviceHeroFor(url: string | null | undefined): ServiceHero | undefined {
  if (!url) return undefined;

  const slug = url
    .split('?')[0]
    .split('#')[0]
    .split('/')
    .filter(Boolean)
    .pop();

  if (!slug) return undefined;

  const key = slug.toLowerCase();
  if (HERO[key]) return HERO[key];

  const hit = KEYWORDS.find(([needle]) => key.includes(needle));
  return hit ? HERO[hit[1]] : undefined;
}
